export interface Cta {
  label: string;
  href: string;
}

export interface NavItem {
  label: string;
  href: string;
}

export interface HeroContent {
  badge: string;
  title: string;
  subtitle: string;
  primaryCta: Cta;
  secondaryCta: Cta;
  trustChips: string[];
}

export interface ProblemCardContent {
  title: string;
  description: string;
}

export interface ServiceBlockContent {
  number: string;
  title: string;
  description: string;
}

export interface MethodologyStepContent {
  title: string;
  description: string;
}

export interface DifferentiationRowContent {
  dimension: string;
  traditional: string;
  solosolar: string;
}

export interface CaseStudyContent {
  client: string;
  project: string;
  challenge: string;
  work: string;
  result: string;
}

export interface IndependenceBlockContent {
  title: string;
  description: string;
}

export interface FaqItemContent {
  question: string;
  answer: string;
}

export interface ContactContent {
  title: string;
  text: string;
  cta: Cta;
  email: string;
}

export interface SectionHeading {
  title: string;
  subtitle: string;
}

export interface SiteContent {
  nav: NavItem[];
  navCta: string;
  hero: HeroContent;
  problem: SectionHeading & { cards: ProblemCardContent[] };
  services: SectionHeading & { blocks: ServiceBlockContent[] };
  methodology: SectionHeading & { steps: MethodologyStepContent[] };
  ahorra: SectionHeading & { intro: string; savingsHighlight: string };
  particulares: SectionHeading;
  differentiation: SectionHeading & {
    columns: { traditional: string; solosolar: string };
    rows: DifferentiationRowContent[];
  };
  caseStudy: SectionHeading & { case: CaseStudyContent };
  independence: SectionHeading & { blocks: IndependenceBlockContent[] };
  faq: SectionHeading & { items: FaqItemContent[] };
  contact: ContactContent;
  footer: { copyright: string; legalLinks: { label: string; href: string }[] };
}

export const content: SiteContent = {
  nav: [
    { label: "Servicios", href: "#servicios" },
    { label: "Metodología", href: "#metodologia" },
    { label: "Factura", href: "#factura" },
    { label: "Casos", href: "#casos" },
    { label: "Contacto", href: "#contacto" },
  ],
  navCta: "Diagnóstico gratuito",
  hero: {
    badge: "No vendemos paneles. Compramos mejor.",
    title: "Ingeniería de compra para proyectos fotovoltaicos",
    subtitle:
      "Ejecutamos el procurement de tu planta solar con due diligence bancaria: RFQ multi-fabricante, verificación de certificaciones y garantías, comparativas CIF/DDP y BOQ bancable.",
    primaryCta: { label: "Solicita diagnóstico gratuito", href: "#contacto" },
    secondaryCta: { label: "Ver metodología", href: "#metodologia" },
    trustChips: [
      "100% independiente de fabricantes",
      "Reportes lender-grade",
      "Sin comisiones",
    ],
  },
  problem: {
    title: "El problema",
    subtitle:
      "Comprar equipos fotovoltaicos sin ingeniería de compra tiene un coste oculto.",
    cards: [
      {
        title: "El fabricante manda",
        description:
          "La mayoría de plantas se compran al único proveedor que conoces. Sin comparar, pagas de más y asumes riesgos de garantía que no puedes verificar.",
      },
      {
        title: "El banco no te financia",
        description:
          "Sin documentación lender-grade (certificaciones IEC, CB, garantías verificadas), tu proyecto no pasa la due diligence de la entidad financiera.",
      },
      {
        title: "Tu equipo pierde semanas",
        description:
          "Un RFQ serio con 4–6 fabricantes, análisis de PIs y BOQ bancable son semanas de trabajo que tu equipo técnico no tiene.",
      },
    ],
  },
  services: {
    title: "Servicios",
    subtitle:
      "Todo lo que tu proyecto necesita antes de firmar un contrato de suministro.",
    blocks: [
      {
        number: "01",
        title: "Procurement Engineering",
        description:
          "RFQs técnicos multi-fabricante, negociación CIF/DDP y condiciones de pago, logística hasta obra.",
      },
      {
        number: "02",
        title: "Due Diligence de proveedores",
        description:
          "Verificación de solvencia real: Tier, IEC 61215/61730, CB, garantías y referencias logísticas en Europa.",
      },
      {
        number: "03",
        title: "BOQ y bancabilidad",
        description:
          "Documentación que la banca acepta: BOQ bancable, análisis de PIs y status reports lender-grade en PDF.",
      },
      {
        number: "04",
        title: "Consultoría técnica-comercial",
        description:
          "Acompañamiento de principio a fin: alcance, análisis de ofertas y soporte hasta el cierre del contrato.",
      },
    ],
  },
  methodology: {
    title: "Metodología",
    subtitle:
      "Un pipeline probado para comprar mejor, desde la RFQ hasta el cierre.",
    steps: [
      {
        title: "RFQ técnico",
        description:
          "Solicitud de oferta con especificaciones completas a 4–6 fabricantes Tier 1/2.",
      },
      {
        title: "Due diligence",
        description:
          "Verificación de certificaciones, garantías y referencias logísticas en Europa.",
      },
      {
        title: "Comparativa",
        description:
          "Análisis de ofertas CIF/DDP, plazos y condiciones de pago frente a frente.",
      },
      {
        title: "BOQ bancable",
        description:
          "Documentación lender-grade con análisis de PIs y status reports.",
      },
      {
        title: "Cierre",
        description:
          "Soporte hasta la firma del contrato directo cliente–fabricante.",
      },
    ],
  },
  ahorra: {
    title: "Factura",
    subtitle:
      "Comprueba cuánto puedes ahorrar en tu factura instalando paneles solares.",
    intro:
      "Sube tu factura de luz y te mostramos cuánto puedes ahorrar instalando paneles.",
    savingsHighlight: "Hasta un 70% de ahorro en tu factura eléctrica",
  },
  particulares: {
    title: "Para particulares",
    subtitle:
      "Calcula el ROI de tu instalación solar y descubre cuándo amortizas la inversión.",
  },
  differentiation: {
    title: "¿Por qué SoloSolar?",
    subtitle:
      "Una forma diferente de comprar equipos fotovoltaicos, alineada contigo.",
    columns: { traditional: "Ingeniería tradicional / EPC", solosolar: "SoloSolar" },
    rows: [
      {
        dimension: "Fuente de ingresos",
        traditional: "Margen en equipos",
        solosolar: "Honorarios fijos",
      },
      {
        dimension: "Imparcialidad",
        traditional: "Condicionada por acuerdos",
        solosolar: "100% independiente OEM",
      },
      {
        dimension: "Alcance",
        traditional: "Diseño y obra",
        solosolar: "Ingeniería de compra",
      },
      {
        dimension: "Documentación",
        traditional: "Informes genéricos",
        solosolar: "Lender-grade",
      },
      {
        dimension: "Acceso a fábricas",
        traditional: "1–2 distribuidores",
        solosolar: "Tier 1/2 con stock EU",
      },
      {
        dimension: "Contrato",
        traditional: "Con su empresa",
        solosolar: "Directo cliente↔fabricante",
      },
    ],
  },
  caseStudy: {
    title: "Caso de éxito",
    subtitle: "Planta FV de 1,8 MWp en suelo, comprada con ingeniería de compra.",
    case: {
      client: "Promotora fotovoltaica en Extremadura",
      project: "Planta FV de 1,8 MWp, suelo",
      challenge:
        "Comprar 1.800 kWp con requisitos de financiación bancaria y sin experiencia previa con fabricantes asiáticos.",
      work: "RFQ a 5 fabricantes Tier 1, verificación de certificaciones IEC/CB y garantías, negociación de 2 PIs (CIF Algeciras y DDP a obra), BOQ bancable con 3 variantes.",
      result:
        "Precio DDP final 0,0985 €/W tras negociación, garantía de producto de 25 años verificada y documentación lender-grade lista para financiación bancaria.",
    },
  },
  independence: {
    title: "Independencia",
    subtitle: "La garantía real de que tu compra es imparcial.",
    blocks: [
      {
        title: "Sin conflicto de interés",
        description:
          "No vendemos equipos ni cobramos comisiones de fabricantes. Nuestro único incentivo es que compres mejor.",
      },
      {
        title: "Contrato directo",
        description:
          "El contrato de suministro va entre tú y el fabricante. Nosotros preparamos, analizamos y asesoramos.",
      },
      {
        title: "Riesgo deslindado",
        description:
          "Cálculos, garantías y plazos los asume cada fabricante por escrito. Nosotros garantizamos el proceso, no el producto.",
      },
    ],
  },
  faq: {
    title: "Preguntas frecuentes",
    subtitle: "Lo que más nos preguntan sobre nuestro trabajo.",
    items: [
      {
        question: "¿SoloSolar cobra comisión de los fabricantes?",
        answer:
          "No. Trabajamos a honorarios de consultoría e ingeniería. No recibimos comisión de ningún fabricante, por lo que nuestra recomendación es siempre imparcial.",
      },
      {
        question: "¿Con quién firmo el contrato de suministro?",
        answer:
          "Directamente con el fabricante elegido. SoloSolar no interviene en el flujo de dinero ni firma contratos de suministro.",
      },
      {
        question: "¿Qué tamaño de proyectos cubrís?",
        answer:
          "Plantas de 0,5 a 50 MWp: autoconsumo industrial, suelo y utility-scale. También hibridación y BESS.",
      },
      {
        question: "¿Qué garantías ofrecen los fabricantes que trabajáis?",
        answer:
          "Solo trabajamos con fabricantes verificables: garantía de producto de 25 años, degradación lineal de 30 años y certificaciones IEC/CB comprobadas.",
      },
      {
        question: "¿Cuánto tarda el proceso completo?",
        answer:
          "De la RFQ al cierre, entre 3 y 6 semanas dependiendo del fabricante y la logística elegida.",
      },
    ],
  },
  contact: {
    title: "¿Tienes un proyecto fotovoltaico en marcha?",
    text: "Dinos potencia, ubicación y plazo. En 48h recibes una propuesta de alcance y honorarios.",
    cta: {
      label: "Pedir diagnóstico gratuito",
      href: "mailto:hola@solosolar.es?subject=Diagn%C3%B3stico%20gratuito%20proyecto%20fotovoltaico",
    },
    email: "hola@solosolar.es",
  },
  footer: {
    copyright: "© SoloSolar",
    legalLinks: [
      { label: "Aviso legal", href: "/aviso-legal" },
      { label: "Privacidad", href: "/politica-privacidad" },
      { label: "Cookies", href: "/politica-cookies" },
    ],
  },
};
